import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FaCalendarCheck,
  FaCalendarDay,
  FaUsers,
  FaCheckCircle,
  FaHandsHelping,
  FaPaperPlane,
  FaPercent,
  FaPhoneAlt,
  FaUserCheck,
  FaUserClock,
  FaUserPlus,
  FaUserTimes,
  FaWhatsapp,
} from 'react-icons/fa';
import api, { formatDateTime, SALON_NAME } from '../services/api';
import { engagementPath } from '../engagementPaths';
import { getEngagementPortal, ENTERPRISE_PARTNER_OPS_APPOINTMENTS } from '../engagementPortal';
import { AppointmentStatus, Loader, SourceBadge, StatCard, StatGrid, WelcomeBar } from '../components/ui';
import { OfferReportCard, UpsellReportCard, WaitingForReplyCard } from '../components/InSalonCards';

export default function Home() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .summary()
      .then(setData)
      .catch((e) => {
        setError(e.message);
        toast.error(`Backend not reachable: ${e.message}`);
      });
  }, []);

  if (!data && !error) return <Loader />;

  if (error && !data) {
    return (
      <div className="de-panel de-fade">
        <div className="de-hint" style={{ marginBottom: 12 }}>
          Could not load the engagement dashboard. Check that the engagement API is running on this server.
        </div>
        <p className="de-small de-muted">{error}</p>
      </div>
    );
  }

  const c = data?.counts || {};
  const setup = data?.setup || {};
  const recent = data?.recent_appointments || [];

  const go = (segment) => () => {
    if (segment === 'appointments' && getEngagementPortal() === 'partner') {
      navigate(ENTERPRISE_PARTNER_OPS_APPOINTMENTS);
      return;
    }
    navigate(engagementPath(segment));
  };
  // Three rows: what happened today, what needs staff now, and how the funnel is doing.
  const groups = [
    {
      title: 'Today',
      cards: [
        { label: "Today's bookings", value: c.today_bookings, icon: <FaCalendarDay />, onClick: go('appointments') },
        { label: 'New leads today', value: c.new_leads_today, icon: <FaUserPlus />, onClick: go('leads') },
        { label: 'Calls today', value: c.calls_today, icon: <FaPhoneAlt />, onClick: go('call-records') },
        {
          label: 'WhatsApp sent today',
          value: c.sent_today,
          icon: <FaPaperPlane />,
          sub: c.failed_today ? `${c.failed_today} failed` : null,
          onClick: go('automations?tab=messages'),
        },
      ],
    },
    {
      title: 'Needs attention',
      cards: [
        {
          label: 'Did they come?',
          value: c.needs_update,
          icon: <FaUserClock />,
          sub: c.needs_update ? 'Still Booked after their time' : null,
          onClick: go('appointments'),
        },
        { label: 'Human intervention', value: c.open_handoffs, icon: <FaHandsHelping />, onClick: go('human-intervention') },
        { label: 'Waiting for a stylist', value: c.unassigned_upcoming, icon: <FaUserCheck />, onClick: go('appointments') },
        { label: 'Upcoming appointments', value: c.upcoming_appointments, icon: <FaCalendarCheck />, onClick: go('appointments') },
      ],
    },
    {
      title: 'Performance',
      cards: [
        {
          label: 'Lead → booking',
          value: `${c.conversion_pct ?? 0}%`,
          icon: <FaPercent />,
          sub:
            c.online_leads != null
              ? `${c.online_booked ?? 0} of ${c.online_leads} enquiries booked (walk-ins not counted)`
              : `${c.booked_leads ?? 0} of ${c.total_leads ?? 0} leads booked`,
          onClick: go('leads'),
        },
        { label: 'Total leads', value: c.total_leads, icon: <FaUsers />, onClick: go('leads') },
        {
          label: 'No-show rate (30 days)',
          value: `${c.no_show_rate_30d ?? 0}%`,
          icon: <FaCheckCircle />,
          sub: 'Missed out of finished appointments',
        },
        {
          label: 'No-shows (7 days)',
          value: c.no_shows_7d,
          icon: <FaUserTimes />,
          sub: c.no_shows_7d ? `${c.no_shows_rebooked_7d || 0} booked again` : null,
          onClick: go('appointments'),
        },
      ],
    },
  ];

  const setupRows = [
    { label: 'Database', ok: !!setup.database, value: setup.database || 'Not connected' },
    { label: 'WhatsApp AI agent', ok: setup.openai, value: setup.openai ? 'Configured' : 'Key or model missing' },
    { label: 'WhatsApp sending', ok: setup.whatsapp, value: setup.whatsapp ? 'Configured' : 'Token or phone number id missing' },
    { label: 'Instagram DMs', ok: setup.instagram, value: setup.instagram ? 'Configured' : 'Instagram access token missing' },
    { label: 'General knowledge files', ok: !!setup.rag_file, value: setup.rag_file || 'None (services and prices come from the service list)' },
    {
      label: 'Automations',
      ok: setup.automations_on > 0,
      value: setup.automations_on > 0 ? `${setup.automations_on} on (Automations page)` : 'None on: open Automations',
    },
  ];

  return (
    <div>
      <WelcomeBar title={`${SALON_NAME} Dashboard`} sub="Customer engagement overview: WhatsApp bookings, voice calls and follow-ups." />

      {error ? <div className="de-callout de-callout--danger de-mb-16">Backend not reachable: {error}</div> : null}

      {data?.queue && (data.queue.leads || data.queue.handoffs || data.queue.conversations) ? (
        <div className="de-callout de-mb-16">
          <strong>HQ queue:</strong> {data.queue.leads} lead{data.queue.leads === 1 ? '' : 's'}, {data.queue.handoffs} request
          {data.queue.handoffs === 1 ? '' : 's'} and {data.queue.conversations} chat{data.queue.conversations === 1 ? '' : 's'} have no branch yet (the
          customer has not chosen one).{' '}
          <Link to={engagementPath('leads')} className="de-purple-text" style={{ fontWeight: 700 }}>
            Open leads →
          </Link>
        </div>
      ) : null}

      {groups.map((group) => (
        <React.Fragment key={group.title}>
          <div className="de-section-label" style={{ margin: '4px 0 8px' }}>
            {group.title}
          </div>
          <StatGrid columns={4} style={{ marginBottom: 16 }}>
            {group.cards.map((card) => (
              <StatCard key={card.label} label={card.label} value={card.value ?? 0} icon={card.icon} sub={card.sub} onClick={card.onClick} />
            ))}
          </StatGrid>
        </React.Fragment>
      ))}

      {Array.isArray(data?.branches) && data.branches.length > 1 ? (
        <div className="de-card de-mb-16">
          <div className="de-card-title">Branches</div>
          <div className="de-table-wrap">
            <table className="de-table">
              <thead>
                <tr>
                  <th>Branch</th>
                  <th>Today&apos;s bookings</th>
                  <th>Upcoming</th>
                  <th>New leads today</th>
                  <th>Leads</th>
                  <th>Lead → booking</th>
                  <th>Calls today</th>
                  <th>Open requests</th>
                </tr>
              </thead>
              <tbody>
                {data.branches.map((b) => (
                  <tr key={b.franchise_id}>
                    <td className="de-td-strong">{b.name}</td>
                    <td>{b.today_bookings}</td>
                    <td>{b.upcoming_appointments}</td>
                    <td>{b.new_leads_today}</td>
                    <td>{b.total_leads}</td>
                    <td>{b.conversion_pct}%</td>
                    <td>{b.calls_today}</td>
                    <td>{b.open_handoffs ? <span className="de-pill de-pill--danger de-pill--sm">{b.open_handoffs}</span> : 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="de-small de-muted de-mt-8">
            Organization totals above count each customer once; here a customer counts at every branch they used.
          </div>
        </div>
      ) : null}

      <div className="de-g2">
        <div className="de-card">
          <div className="de-row de-row--between" style={{ marginBottom: 6 }}>
            <div className="de-card-title" style={{ marginBottom: 0 }}>
              Recent Appointments
            </div>
            <Link
              to={
                getEngagementPortal() === 'partner'
                  ? ENTERPRISE_PARTNER_OPS_APPOINTMENTS
                  : engagementPath('appointments')
              }
              className="de-small de-purple-text"
              style={{ fontWeight: 700 }}
            >
              View all →
            </Link>
          </div>
          {recent.length === 0 ? (
            <div className="de-muted de-small" style={{ padding: '20px 0', textAlign: 'center' }}>
              No appointments yet
            </div>
          ) : (
            recent.map((a) => (
              <div key={a.id} className="de-line-item">
                <div style={{ minWidth: 0 }}>
                  <div className="de-strong de-ellipsis">{a.customer_name}</div>
                  <div className="de-small de-muted de-ellipsis">
                    {a.staff_name || 'Stylist not assigned'} · {a.service_name}
                    {Array.isArray(data?.branches) && data.branches.length > 1 && a.branch_name ? ` · ${a.branch_name.split(' (')[0]}` : ''}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div className="de-row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                    <SourceBadge source={a.source} size={15} />
                    <AppointmentStatus status={a.status} />
                  </div>
                  <div className="de-small de-muted" style={{ marginTop: 3 }}>
                    {formatDateTime(a.starts_at)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="de-card">
          <div className="de-card-title">Lead Channels</div>
          <div className="de-line-item">
            <span className="de-row" style={{ gap: 8 }}>
              <FaWhatsapp size={16} color="#25D366" />
              <span className="de-strong" style={{ color: '#1fa855' }}>
                WhatsApp bookings
              </span>
            </span>
            <span className="de-strong">{c.whatsapp_appointments ?? 0}</span>
          </div>
          <div className="de-line-item">
            <span className="de-row" style={{ gap: 8 }}>
              <FaPhoneAlt size={13} color="#6b4c9a" />
              <span className="de-strong" style={{ color: '#6b4c9a' }}>
                Voice bookings
              </span>
            </span>
            <span className="de-strong">{c.sarvam_appointments ?? 0}</span>
          </div>
          <div className="de-line-item">
            <span className="de-muted">WhatsApp conversations</span>
            <span className="de-strong">{c.conversations ?? 0}</span>
          </div>
          <div className="de-line-item">
            <span className="de-muted">Open leads (not booked yet)</span>
            <span className="de-strong">{c.open_leads ?? 0}</span>
          </div>
          <div className="de-line-item">
            <span className="de-muted">Services taken</span>
            <span className="de-strong de-gold-text">{c.services_taken ?? 0}</span>
          </div>
          <div className="de-line-item">
            <span className="de-muted">Active staff</span>
            <span className="de-strong">{c.active_staff ?? 0}</span>
          </div>
        </div>
      </div>

      <WaitingForReplyCard />
      <UpsellReportCard />
      <OfferReportCard />

      <div className="de-card de-mt-16">
        <div className="de-card-title">Setup</div>
        {setupRows.map((row) => (
          <div key={row.label} className="de-line-item">
            <div>
              <div className="de-strong">{row.label}</div>
              <div className="de-small de-muted">{row.value}</div>
            </div>
            <span className={`de-pill ${row.ok ? 'de-pill--ok' : 'de-pill--warn'}`}>{row.ok ? 'Active' : 'Needs setup'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
